import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Phone, CheckCircle, ArrowRight, Star, Shield, Clock, 
  PoundSterling, Users, Home, FileText, Wrench, Eye,
  BadgeCheck, Banknote, Key, MessageSquare, Calendar
} from 'lucide-react';

export const LandlordServices = () => {
  const services = [
    {
      title: "Tenant Finding",
      description: "We find quality tenants fast. Professional marketing, thorough referencing, and accompanied viewings.",
      icon: Users,
      features: ["Professional photography", "Major portal listings", "Full referencing", "Accompanied viewings"]
    },
    {
      title: "Full Management",
      description: "Hands-off landlording. We handle everything from tenant queries to maintenance and rent collection.",
      icon: Home,
      features: ["Rent collection", "24/7 maintenance", "Regular inspections", "Tenant management"]
    },
    {
      title: "Rent Collection",
      description: "Guaranteed rent on time, every time. We chase arrears so you don't have to.",
      icon: Banknote,
      features: ["Monthly statements", "Arrears management", "Direct bank transfer", "Full transparency"]
    },
    {
      title: "Legal Compliance",
      description: "Stay compliant with all UK landlord regulations. We handle the paperwork and certificates.",
      icon: Shield,
      features: ["Gas safety", "EPC certificates", "Electrical checks", "Right to Rent"]
    },
    {
      title: "Property Maintenance",
      description: "Trusted contractors, competitive rates. We keep your property in top condition.",
      icon: Wrench,
      features: ["Vetted contractors", "Emergency repairs", "Quotes approval", "Quality checks"]
    },
    {
      title: "Regular Inspections",
      description: "Protect your investment with quarterly property inspections and detailed reports.",
      icon: Eye,
      features: ["Photo reports", "Condition updates", "Issue flagging", "Tenant feedback"]
    }
  ];

  const testimonials = [
    {
      quote: "Harry & Jacobs took all the stress out of managing my rental. Communication is excellent and rent is always on time.",
      location: "Landlord, Harrow"
    },
    {
      quote: "Professional service from start to finish. They found great tenants within two weeks.",
      location: "Landlord, Wembley"
    },
    {
      quote: "I've used other agents before but none compare. They genuinely care about my property.",
      location: "Landlord, Stanmore"
    },
    {
      quote: "Excellent communication and very proactive. My property has never been vacant for long.",
      location: "Landlord, Pinner"
    }
  ];

  const stats = [
    { value: "Dozens", label: "Properties Managed", subtext: "Across Harrow & NW London" },
    { value: "Fast", label: "Tenant Placement", subtext: "Strong local demand" },
    { value: "98%", label: "Rent Collected", subtext: "On time, every month" },
    { value: "Referrals", label: "How We Grow", subtext: "From happy landlords" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#404040] to-[#2a2a2a] text-white py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-block bg-[#FF8C42] text-white text-sm font-semibold px-4 py-1 rounded-full mb-6">
              HARROW'S TRUSTED LETTING AGENTS
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Landlord Services That Put Your Investment First
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Whether you want hands-off management or just need help finding tenants, we've got you covered. Local expertise, transparent fees, and landlords who keep coming back.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/landlord/free-valuation">
                <Button className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white px-8 py-6 text-lg">
                  Get Free Valuation <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="tel:02081898889">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  <Phone className="w-5 h-5 mr-2" /> 0208 189 8889
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-[#FF8C42] py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-white text-center">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <div className="text-3xl font-bold">{stat.value}</div>
                <div className="font-medium">{stat.label}</div>
                <div className="text-sm opacity-80">{stat.subtext}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#404040] mb-4">Why Landlords Choose Harry & Jacobs</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Most of our new instructions come from referrals and repeat landlords. Here's why they trust us with their properties.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF8C42]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-[#FF8C42]" />
              </div>
              <h3 className="text-xl font-bold text-[#404040] mb-2">Minimal Void Periods</h3>
              <p className="text-gray-600">
                Strong tenant demand in Harrow means your property won't sit empty. We work fast to find quality tenants.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF8C42]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <PoundSterling className="w-8 h-8 text-[#FF8C42]" />
              </div>
              <h3 className="text-xl font-bold text-[#404040] mb-2">Maximised Rental Income</h3>
              <p className="text-gray-600">
                We know the local market inside out. Get the rent your property deserves with expert pricing.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF8C42]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#FF8C42]" />
              </div>
              <h3 className="text-xl font-bold text-[#404040] mb-2">Full Compliance Support</h3>
              <p className="text-gray-600">
                Never worry about regulations. We ensure your property meets all legal requirements.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#404040] mb-4">Our Landlord Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From tenant finding to full management, choose the level of support that works for you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow border-t-4 border-t-[#FF8C42]">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-[#FF8C42]/10 rounded-lg flex items-center justify-center mb-4">
                    <service.icon className="w-6 h-6 text-[#FF8C42]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#404040] mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Fee Structure */}
      <div className="py-16 bg-[#404040] text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Transparent Pricing</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              No hidden fees, no surprises. Choose the service level that suits you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Tenant Find Only */}
            <Card className="bg-white text-[#404040]">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold">Tenant Finding</h3>
                  <p className="text-gray-500">We find the tenant, you manage</p>
                </div>
                <div className="text-center mb-6">
                  <span className="text-4xl font-bold text-[#FF8C42]">55%</span>
                  <span className="text-gray-500 ml-2">of first month's rent</span>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Professional photography
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Rightmove & Zoopla listings
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Full tenant referencing
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Tenancy agreement preparation
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Deposit registration
                  </li>
                </ul>
                <Link to="/landlord/free-valuation" className="block">
                  <Button className="w-full bg-[#404040] hover:bg-[#2a2a2a]">
                    Get Started
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Full Management */}
            <Card className="bg-[#FF8C42] text-white border-0 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-white text-[#FF8C42] text-xs font-bold px-3 py-1 rounded-full">
                MOST POPULAR
              </div>
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold">Full Management</h3>
                  <p className="text-white/80">Complete hands-off service</p>
                </div>
                <div className="text-center mb-6">
                  <span className="text-4xl font-bold">12%</span>
                  <span className="text-white/80 ml-2">of monthly rent</span>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-white" />
                    Everything in Tenant Finding
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-white" />
                    Monthly rent collection
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-white" />
                    Maintenance coordination
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-white" />
                    Quarterly inspections
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-white" />
                    Tenant communication
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-white" />
                    Compliance management
                  </li>
                </ul>
                <Link to="/landlord/free-valuation" className="block">
                  <Button className="w-full bg-white text-[#FF8C42] hover:bg-gray-100">
                    Get Started
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#404040] mb-4">What Our Landlords Say</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="bg-white">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#FF8C42] text-[#FF8C42]" />
                    ))}
                  </div>
                  <p className="text-gray-600 italic mb-4">"{testimonial.quote}"</p>
                  <p className="text-[#FF8C42] font-medium text-sm">— {testimonial.location}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Areas We Cover */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#404040] mb-4">Areas We Cover</h2>
            <p className="text-gray-600">Serving landlords across Harrow and North West London</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
            {['Harrow', 'Wembley', 'Stanmore', 'Pinner', 'Northwood', 'Ruislip', 'Edgware', 'Kingsbury', 'Kenton', 'Wealdstone', 'Rayners Lane', 'South Harrow', 'North Harrow', 'Hatch End'].map((area, idx) => (
              <span key={idx} className="bg-gray-100 text-[#404040] px-4 py-2 rounded-full text-sm font-medium">
                {area}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-[#FF8C42] py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Maximise Your Rental Income?
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation rental valuation and see what your property could achieve.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/landlord/free-valuation">
              <Button className="bg-white text-[#FF8C42] hover:bg-gray-100 px-8 py-6 text-lg font-semibold">
                Get Free Valuation <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:02081898889">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                <Phone className="w-5 h-5 mr-2" /> 0208 189 8889
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandlordServices;
